// Phase 6.8: small helper to track the agent's current impersonation
// session in sessionStorage. We deliberately use sessionStorage so the
// context dies on browser/tab close — closing the tab does NOT revoke
// the BE session (the agent must explicitly End it), but it does mean
// the FE forgets the token, which keeps it from being silently
// re-used after the agent walks away.

export interface ImpersonationContext {
  accessToken: string;
  jti: string;
  target: { _id: string; email: string; name?: string };
  agent: { _id: string; email: string };
  temporaryAccess?: {
    email: string;
    accessCode: string;
    loginUrl: string;
    expiresAt: string;
  };
  startedAt: number; // epoch ms
  expiresAt: number; // epoch ms
  ttlSeconds: number;
  reason: string;
}

const KEY = 'kayfa.impersonation.context';

export function readImpersonationContext(): ImpersonationContext | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationContext;
    if (
      typeof parsed?.accessToken !== 'string' ||
      typeof parsed?.expiresAt !== 'number'
    ) {
      return null;
    }
    // Drop if already expired client-side.
    if (parsed.expiresAt <= Date.now()) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeImpersonationContext(ctx: ImpersonationContext): void {
  sessionStorage.setItem(KEY, JSON.stringify(ctx));
  // Fire a same-window event so any banner mounted elsewhere updates.
  window.dispatchEvent(new CustomEvent('kayfa-impersonation-changed'));
}

export function clearImpersonationContext(): void {
  sessionStorage.removeItem(KEY);
  window.dispatchEvent(new CustomEvent('kayfa-impersonation-changed'));
}
